import { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, MessageSquare, FileText, ListTodo, Users, Video, ArrowLeft, Calendar, type LucideIcon } from 'lucide-react';
import { apiClient } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useIsMobile } from '@/hooks/use-media-query';
import { AuroraGlassPanel } from '@easyflow/ui-frontend/components/ui/aurora-glass-panel';
import MobileWorkgroupHub from '@/pages/mobile/MobileWorkgroupHub';
import IdentityEditor from './IdentityEditor';
import ChatTab from './ChatTab';
import FilesTab from './FilesTab';
import TasksTab from './TasksTab';
import MembersTab from './MembersTab';
import HuddleTab from './HuddleTab';
import EventsTab from './EventsTab';
import type { Workgroup } from './types';

type TabKey = 'chat' | 'huddle' | 'tasks' | 'events' | 'files' | 'members';

const TABS: Array<{ key: TabKey; label: string; icon: LucideIcon }> = [
  { key: 'chat', label: 'Chat', icon: MessageSquare },
  { key: 'huddle', label: 'Video Call Live', icon: Video },
  { key: 'tasks', label: 'Tasks', icon: ListTodo },
  { key: 'events', label: 'Eventi', icon: Calendar },
  { key: 'files', label: 'Files', icon: FileText },
  { key: 'members', label: 'Membri', icon: Users },
];

export default function WorkgroupHub() {
  const isMobile = useIsMobile();
  return isMobile ? <MobileWorkgroupHub /> : <WorkgroupHubDesktop />;
}

function WorkgroupHubDesktop() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [workgroup, setWorkgroup] = useState<Workgroup | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<TabKey>('chat');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get<Workgroup>(`/messenger/workgroups/${id}`);
      setWorkgroup(res.data ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore caricamento');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const currentUserId = user?.id ?? null;
  const isOwner = !!workgroup && !!currentUserId && workgroup.owner_id === currentUserId;
  const isAdmin = workgroup?.my_role === 'admin' || isOwner;
  const canEdit = isAdmin;

  const tenantSlug = useMemo(() => window.location.pathname.split('/')[1] || '', []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <Loader2 size={28} className="animate-spin" />
      </div>
    );
  }

  if (error || !workgroup) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-400 mb-3">{error ?? 'Workgroup non trovato'}</p>
        <button onClick={() => navigate(`/${tenantSlug}/messenger/gruppi`)} className="text-blue-400 hover:text-blue-300 text-sm">
          ← Torna alla lista
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Task #1665 — header workgroup in pannello glass Aurora. */}
      <AuroraGlassPanel as="header" intensity="medium" className="p-4 border-b border-border">
        <button
          onClick={() => navigate(`/${tenantSlug}/messenger/gruppi`)}
          className="text-xs text-muted-foreground hover:text-foreground mb-3 flex items-center gap-1"
          aria-label="Torna alla lista workgroup"
        >
          <ArrowLeft size={12} /> Tutti i workgroup
        </button>
        <IdentityEditor
          workgroup={workgroup}
          canEdit={canEdit}
          isOwner={isOwner}
          onUpdated={(wg) => setWorkgroup(wg)}
        />
      </AuroraGlassPanel>

      <div role="tablist" aria-label="Sezioni workgroup" className="flex border-b border-border bg-card/50">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            id={`wg-tab-${key}`}
            role="tab"
            aria-selected={tab === key}
            aria-controls={`wg-panel-${key}`}
            tabIndex={tab === key ? 0 : -1}
            onClick={() => setTab(key)}
            className={`flex-1 px-4 py-3 text-sm flex items-center justify-center gap-2 border-b-2 transition ${
              tab === key
                ? 'border-primary text-foreground bg-card/50'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-card/30'
            }`}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto">
        {TABS.map(({ key }) => (
          <div
            key={key}
            id={`wg-panel-${key}`}
            role="tabpanel"
            aria-labelledby={`wg-tab-${key}`}
            hidden={tab !== key}
            className="h-full"
          >
            {tab === key && key === 'chat'    && <ChatTab    workgroup={workgroup} currentUserId={currentUserId} canManage={isAdmin} />}
            {tab === key && key === 'huddle'  && <HuddleTab  workgroup={workgroup} currentUserId={currentUserId} />}
            {tab === key && key === 'tasks'   && <TasksTab   workgroup={workgroup} canManage={isAdmin} />}
            {tab === key && key === 'events'  && <EventsTab  workgroup={workgroup} canManage={isAdmin} />}
            {tab === key && key === 'files'   && <FilesTab   workgroup={workgroup} canManage={isAdmin} />}
            {tab === key && key === 'members' && <MembersTab workgroup={workgroup} canManage={isAdmin} currentUserId={currentUserId} />}
          </div>
        ))}
      </div>
    </div>
  );
}
